<script setup lang="ts">
  import { useFullscreen } from '@vueuse/core';
  import dayjs from 'dayjs';
  import { Workbook } from 'exceljs';
  import { computed, onBeforeMount, ref, useTemplateRef } from 'vue';

  import { Button } from '@ui/components/button';
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@ui/components/select';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { downloadFile } from '@ui/utils/utils';

  const props = defineProps<{ url: string; fileName: string; mimeType?: string }>();
  const xlsx_table_ref = useTemplateRef<HTMLElement>('xlsx_table');
  const DEFAULT_OPTION = 'text-2xs';

  const { toggle } = useFullscreen(xlsx_table_ref);
  const xlsxTableHtml = ref('');
  const xlsxTableError = ref(false);
  const workbook = ref<Workbook | null>(null);
  const sheetNames = ref<string[]>([]);
  const selectedSheet = ref<string>('');

  const expectedXlsxTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'binary/octet-stream',
  ];

  const options = ['text-3xs', 'text-2xs', 'text-xs', 'text-sm', 'text-base', 'text-lg', 'text-xl'];

  const selectedOption = ref(DEFAULT_OPTION);

  const selectedOptionIndex = computed(() => options.indexOf(selectedOption.value));

  const disableMinus = computed(() => {
    return selectedOptionIndex.value === 0;
  });

  const disablePlus = computed(() => {
    return selectedOptionIndex.value === options.length - 1;
  });

  const decreaseSize = () => {
    selectedOption.value = options[selectedOptionIndex.value - 1];
  };

  const increaseSize = () => {
    selectedOption.value = options[selectedOptionIndex.value + 1];
  };

  const downloadAttachment = () => {
    if (props.url) {
      downloadFile(props.url, props.fileName);
    }
  };

  const escapeHtml = (s: string): string =>
    s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/[\r\n\t]/g, ' ');

  const resolveCellValue = (value: any): string => {
    if (value == null) return '';
    // Formula cell: { formula: '...', result: ... } or { sharedFormula: '...', result: ... }
    if (typeof value === 'object' && ('formula' in value || 'sharedFormula' in value))
      return value.result != null ? resolveCellValue(value.result) : '';
    // Rich text cell: { richText: [{ text: '...' }, ...] }
    if (typeof value === 'object' && Array.isArray(value.richText))
      return value.richText.map((r: any) => r.text ?? '').join('');
    // Hyperlink cell: { hyperlink: '...', text: '...' } — text may itself be rich-text
    if (typeof value === 'object' && 'hyperlink' in value)
      return resolveCellValue(value.text ?? value.hyperlink ?? '');
    // Error cell: { error: '#REF!' }
    if (typeof value === 'object' && 'error' in value) return value.error ?? '';
    if (value instanceof Date) {
      const hasTime = Boolean(value.getHours() || value.getMinutes() || value.getSeconds());
      return dayjs(value).format(hasTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD');
    }
    return String(value);
  };

  const renderSheet = (sheetName: string) => {
    if (!workbook.value) return;

    try {
      const worksheet = workbook.value.getWorksheet(sheetName);
      if (!worksheet) {
        xlsxTableHtml.value = '<div class="p-4 text-muted-foreground">Sheet not found</div>';
        return;
      }

      const data: string[][] = [];
      worksheet.eachRow((row) => {
        const rowData: string[] = [];
        row.eachCell({ includeEmpty: true }, (cell) => {
          rowData.push(resolveCellValue(cell.value));
        });
        data.push(rowData);
      });

      if (!data || data.length === 0) {
        xlsxTableHtml.value = '<div class="p-4 text-muted-foreground">Sheet is empty</div>';
        return;
      }

      let html = '<table class="bg-background min-w-full text-left text-muted-foreground">';
      html += '<thead class="text-foreground capitalize sticky top-0 bg-background z-10"><tr>';

      // Header row
      const headerRow = data[0];
      headerRow.forEach((cell) => {
        const cellValue = escapeHtml(cell);
        html += `<th class="px-2 py-2 max-w-[200px]"><span class="truncate block" title="${cellValue}">${cellValue}</span></th>`;
      });
      html += '</tr></thead><tbody class="bg-background">';

      // Data rows
      for (let i = 1; i < data.length; i++) {
        if (data[i].every((c) => c === '')) continue;
        html += '<tr class="bg-background border-t">';
        data[i].forEach((cell) => {
          const cellValue = escapeHtml(cell);
          html += `<td class="px-2 py-2 max-w-[400px]"><span class="truncate block" title="${cellValue}">${cellValue}</span></td>`;
        });
        html += '</tr>';
      }

      html += '</tbody></table>';
      xlsxTableHtml.value = html;
    } catch (error) {
      console.error('Error rendering sheet:', error);
      xlsxTableError.value = true;
    }
  };

  const parseXlsx = async () => {
    try {
      if (!props.url) {
        xlsxTableError.value = true;
        return;
      }

      const response = await fetch(props.url);
      const arrayBuffer = await response.arrayBuffer();

      const wb = new Workbook();
      await wb.xlsx.load(arrayBuffer);
      workbook.value = wb;

      sheetNames.value = wb.worksheets.map((sheet) => sheet.name);

      if (sheetNames.value.length > 0) {
        selectedSheet.value = sheetNames.value[0];
        renderSheet(selectedSheet.value);
      }
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      xlsxTableError.value = true;
    }
  };

  const handleSheetChange = (sheetName: string) => {
    selectedSheet.value = sheetName;
    renderSheet(sheetName);
  };

  onBeforeMount(() => {
    const isValidType =
      expectedXlsxTypes.includes(props.mimeType || '') ||
      props.fileName?.endsWith('.xlsx') ||
      props.fileName?.endsWith('.xls');

    if (isValidType) {
      parseXlsx();
    }
  });
</script>

<template>
  <div ref="xlsx_table" class="flex h-full flex-col">
    <div
      class="flex flex-nowrap items-center justify-between space-x-3 p-3 rounded-tr-sm rounded-tl-sm"
    >
      <div class="flex items-center gap-x-1">
        <Button variant="outline" size="icon" :disabled="disableMinus" @click="decreaseSize">
          <i class="fa fa-solid fa-minus text-2xs!"></i>
        </Button>

        <Button variant="ghost" size="icon" @click="selectedOption = DEFAULT_OPTION">
          <i class="far fa-text-size text-2xs!"></i>
        </Button>

        <Button variant="outline" size="icon" :disabled="disablePlus" @click="increaseSize">
          <i class="fa fa-solid fa-plus text-2xs!"></i>
        </Button>

        <Select
          v-if="sheetNames.length > 1"
          :model-value="selectedSheet"
          @update:model-value="handleSheetChange"
        >
          <SelectTrigger class="w-[180px]">
            <SelectValue placeholder="Select sheet" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="sheet in sheetNames" :key="sheet" :value="sheet">
              {{ sheet }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div class="flex items-center space-x-2">
        <Tooltip>
          <TooltipTrigger as-child>
            <Button variant="outline" class="w-8 h-8" @click="downloadAttachment">
              <i class="fa-solid fa-download"></i>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <Button variant="link" size="inline" class="text-2xs" @click="downloadAttachment">
              {{ fileName }}
            </Button>
          </TooltipContent>
        </Tooltip>

        <slot name="xlsx_viewer_actions"></slot>

        <Tooltip>
          <TooltipTrigger as-child>
            <Button variant="outline" class="w-8 h-8" @click="toggle">
              <i class="fa-solid fa-expand text-2xs!"></i>
            </Button>
          </TooltipTrigger>
          <TooltipContent class="text-2xs" side="bottom">View fullscreen</TooltipContent>
        </Tooltip>
      </div>
    </div>
    <div class="flex-1 min-h-0 overflow-auto mb-4">
      <div
        class="w-full relative"
        :class="{ 'border rounded-br-sm rounded-bl-sm': !xlsxTableError }"
      >
        <div v-if="xlsxTableError" class="border-t pt-4">
          Error parsing Excel file
          <span
            class="font-medium text-blue hover:text-blue/50 cursor-pointer"
            @click="downloadAttachment"
          >
            Download {{ fileName }}
          </span>
        </div>

        <div class="bg-background w-full h-full">
          <div :class="selectedOption" v-html="xlsxTableHtml"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  :fullscreen {
    display: flex;
    flex-direction: column;
    width: 100vw;
    height: 100vh;
    padding: 2rem;
    background: hsl(var(--background));
  }

  :fullscreen .overflow-auto {
    flex: 1;
    width: 100%;
    height: 100%;
  }

  :fullscreen table thead {
    position: sticky;
    top: 0;
    background: hsl(var(--background));
    z-index: 10;
  }

  table thead {
    position: sticky;
    top: 0;
    background: hsl(var(--background));
    z-index: 10;
  }
</style>
